// FludeePage.jsx (FULLY RESPONSIVE FIX)
// ✅ fixes: stacking on tablet, image/content widths, overflow, consistent alignment
// ✅ keeps: your design + animations + cards

import * as React from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  Stack,
  Paper,
} from "@mui/material";
import { alpha, useTheme, keyframes } from "@mui/material/styles";

import dashboardImg from "../assets/img/IMG_20250227_105446.jpg";
import dashboardImg1 from "../assets/img/IMG_20250227_123315.jpg";
import SingleImageHero from "./imgful";

// ✅ zoom in/out animation for button (Chip)
const pulseZoom = keyframes`
  0%   { transform: scale(1);   box-shadow: 0 10px 22px rgba(0,0,0,0.12); }
  50%  { transform: scale(1.08); box-shadow: 0 16px 30px rgba(0,0,0,0.22); }
  100% { transform: scale(1);   box-shadow: 0 10px 22px rgba(0,0,0,0.12); }
`;

export default function FludeePage() {
  const theme = useTheme();

  // 🔥 DARK SECTION
  const DarkSection = ({ children }) => (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 7, md: 10 },
        background: `linear-gradient(180deg, #0b1220 0%, ${alpha(
          "#0b1220",
          0.92
        )} 100%)`,
      }}
    >
      <Container maxWidth="xl">{children}</Container>
    </Box>
  );

  const ImageCard = ({ src, alt }) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${alpha("#fff", 0.12)}`,
        boxShadow: `0 24px 60px ${alpha("#000", 0.4)}`,
        width: "100%",
        maxWidth: { xs: 520, sm: 640, md: 760 }, // ✅ responsive cap
        mx: "auto",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        sx={{
          width: "100%",
          height: { xs: 220, sm: 280, md: 360, lg: 420 }, // ✅ smoother scaling
          objectFit: "cover",
          display: "block",
        }}
      />
    </Paper>
  );

  const BulletList = ({ items }) => (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 2.2,
        color: alpha("#fff", 0.88),
        lineHeight: 1.9,
        "& li": { mb: 1 },
      }}
    >
      {items.map((t, i) => (
        <li key={i}>
          <Typography component="span" sx={{ color: "inherit" }}>
            {t}
          </Typography>
        </li>
      ))}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", overflowX: "hidden" }}>
      {/* ✅ HEADER */}
      <Box
        sx={{
          py: { xs: 6, sm: 7, md: 10 },
          textAlign: "center",
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          background: `linear-gradient(
            180deg,
            ${alpha("#38bdf8", 0.45)} 0%,
            ${alpha("#e0f2fe", 1)} 60%,
            ${alpha(theme.palette.background.default, 1)} 100%
          )`,
        }}
      >
        <Container>
          <Stack spacing={2} alignItems="center">
            <Chip
              label="Launching Soon"
              sx={{
                fontWeight: 900,
                px: 1.6,
                py: 0.8,
                borderRadius: 999,
                backgroundColor: "#facc15",
                color: "#0b1220",
                border: `1px solid ${alpha("#0b1220", 0.18)}`,
                animation: `${pulseZoom} 1.6s ease-in-out infinite`,
                transformOrigin: "center",
              }}
            />

            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.5,
                fontSize: { xs: 34, sm: 44, md: 56 }, // ✅ responsive heading
              }}
            >
              Fludee
            </Typography>

            <Typography sx={{ color: alpha("#0b1220", 0.75) }}>
             Smart Fluid Management Software Device
            </Typography>
          </Stack>
        </Container>
      </Box>
      <SingleImageHero/>

      {/* ✅ SECTION 1 (RESPONSIVE FIX: SM se side-by-side) */}
      <DarkSection>
        <Grid
          container
          spacing={{ xs: 4, md: 5 }}
          alignItems="center"
          justifyContent="center"
        >
          {/* LEFT CONTENT */}
          <Grid item xs={12} sm={6} md={5}>
            <Stack spacing={2} alignItems="flex-start" sx={{ textAlign: "left" }}>
              <Typography variant="h4" fontWeight={900} color="white">
                Under Development
              </Typography>

              <Typography sx={{ color: alpha("#fff", 0.85), lineHeight: 1.8 }}>
                Fludee is a medical device under development to support patient care
                through structured <br/>monitoring, clinical insight, and guided workflows.
              </Typography>

              <BulletList
                items={[
                  "Wi-Fi enabled system that displays real-time fluid therapy speed and tracks input 24×7.",
                  "Improves visibility into patient status and reduces gaps in routine care processes.",
                  "Supports timely decisions with safety-focused engineering and validation planning.",
                  "Under active development with refinement and optimization of core components.",
                  "Public release planned soon with phased, purpose-driven feature rollout.",
                ]}
              />
            </Stack>
          </Grid>

          {/* RIGHT IMAGE (center always) */}
          <Grid
            item
            xs={12}
            sm={6}
            md={7}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ImageCard src={dashboardImg} alt="Fludee Dashboard" />
          </Grid>
        </Grid>
      </DarkSection>

      {/* ✅ SECTION 2 (RESPONSIVE FIX) */}
      <DarkSection>
        <Grid
          container
          spacing={{ xs: 4, md: 5 }}
          alignItems="center"
          justifyContent="center"
        >
          {/* LEFT IMAGE */}
          <Grid
            item
            xs={12}
            sm={6}
            md={7}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              order: { xs: 1, md: 1 }, // ✅ keep image first on all sizes
            }}
          >
            <ImageCard src={dashboardImg1} alt="Device Software UI" />
          </Grid>

          {/* RIGHT CONTENT */}
          <Grid item xs={12} sm={6} md={5} sx={{ order: { xs: 2, md: 2 } }}>
            <Stack spacing={2} alignItems="flex-start" sx={{ textAlign: "left" }}>
              <Typography variant="h4" fontWeight={900} color="white">
                Why Fludee?
              </Typography>

              <BulletList
                items={[
                  "Integrated Care Display: Combines device data and workflow info into one reliable interface.",
                  "Purpose-Focused Design: Built for high-pressure environments with clarity and ease of use.",
                  "Device-Driven Intelligence: Designed around device integration for consistent monitoring and informed decisions.",
                ]}
              />

              <Box>
                <a
                  href="https://fludee.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#90caf9",
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#FFD700")}
                  onMouseLeave={(e) => (e.target.style.color = "#90caf9")}
                >
                  Learn more on our partner platform →
                </a>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DarkSection>
    </Box>
  );
}
